import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooterCompact from "@/components/landing/SiteFooterCompact";
import { POSTS } from "@/pages/blog/posts";
import { P, H3, H4, UL, Callout, Quote, Table } from "@/pages/knowledge/Atoms";
import { ArrowLeft, Clock } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} — Tradecure Blog`;
      const meta = document.querySelector('meta[name="description"]');
      const desc = post.excerpt;
      if (meta) meta.setAttribute("content", desc);
      else {
        const m = document.createElement("meta");
        m.name = "description";
        m.content = desc;
        document.head.appendChild(m);
      }
    }
    window.scrollTo({ top: 0 });
  }, [post]);

  if (!post) {
    return (
      <main className="bg-white text-[#0f172a]">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-5 md:px-8 py-20 text-center">
          <h1 className="font-heading text-4xl">Post not found</h1>
          <Link to="/blog" className="text-[#084392] underline mt-3 inline-block">
            ← Back to blog
          </Link>
        </div>
        <SiteFooterCompact />
      </main>
    );
  }

  return (
    <main data-testid="blog-post" className="bg-white text-[#0f172a]">
      <SiteHeader />

      <article className="mx-auto max-w-3xl px-5 md:px-8 py-14">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#084392] mb-6"
        >
          <ArrowLeft size={14} /> Back to blog
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.22em]">
            {post.tag.toUpperCase()}
          </div>
          <h1 className="font-heading mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 text-sm font-mono text-[#475569]">
            <span>{post.date}</span>
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {post.readMins} min read
            </span>
          </div>
        </motion.header>

        <div
          className="mt-8 h-52 rounded-2xl relative overflow-hidden"
          style={{ background: post.cover }}
        >
          <div className="absolute inset-0 dot-bg opacity-30" />
        </div>

        {/* Body */}
        <div className="mt-10">
          <P>
            <b>The illusion of luck and the power of preparation.</b> While a
            sprinkle of luck might open doors in the stock market, true success
            hinges on a disciplined mind&rsquo;s ability to protect capital
            until opportunity knocks. Understanding the intricate ecosystem of
            the stock market isn&rsquo;t just about recognising trends — it&rsquo;s
            about grasping the underlying forces that shape investor behaviour.
            This is a look at the current landscape of the Indian stock market,
            its immense growth potential, and the critical distinctions between
            its key players — from the hopeful retailer to the sophisticated
            institution.
          </P>

          <H3>The exploding Indian stock market: potential and pitfalls</H3>
          <P>
            The Indian stock market is on a remarkable growth trajectory,
            boasting over <b>10 crore</b> individual participants, with{" "}
            <b>4.5 crore</b> actively trading and <b>1 crore</b> engaged in
            derivative trading. With India&rsquo;s rising internet penetration
            and a population exceeding 150 crore, the room for expansion is
            astronomical. This contrasts sharply with developed markets like the
            USA, where a smaller population already shows significant
            participation. At a projected <b>CAGR of 34%</b>, India is expected
            to host over <b>35 crore</b> individual participants by 2030-32.
          </P>
          <P>
            But this growth story has a sobering counterpoint: a staggering{" "}
            <b>93% of retailers report losses</b>, with an average individual
            loss of <b>₹2 lakh</b>. The F&amp;O segment alone witnessed{" "}
            <b>₹75,000 crore</b> in retail losses in FY24. The crucial question
            — are the profitable 7% consistently the same people year after
            year? <b>No.</b>
          </P>

          <H3>Decoding market participants: a stratified landscape</H3>
          <UL>
            <li>
              <b>Retailers</b> — the vast majority of individual investors.
            </li>
            <li>
              <b>Operators</b> — individuals or groups with substantial capital
              who strategically manipulate stock prices, often through multiple
              hidden accounts.
            </li>
            <li>
              <b>Institutions</b> — large entities like FPIs and proprietary
              trading firms, leveraging sophisticated strategies and technology.
            </li>
          </UL>

          <H3>Trading styles: from prediction to precision</H3>
          <H4>Retailers</H4>
          <P>
            Retail investors primarily engage in <b>prediction-based trading</b>
            , whether through intuitive guesswork or rudimentary data analysis.
            In equities, this involves chart analysis, indicator usage and
            forecasting price movements. In options trading, they opt for either{" "}
            <b>&ldquo;naked&rdquo; (single-leg) trading</b> — mirroring equity
            strategies — or <b>strategic (multi-leg) trading</b>.
          </P>
          <P>
            While strategic options trading involves analysing payoffs,
            probabilities, risk-reward ratios and backtesting to minimise risk,
            naked trading often becomes a chase for 20–200% short-term gains.
            &ldquo;Eyes-closed&rdquo; prediction without risk awareness is the
            riskiest. Naked trading with some analysis is moderately risky but
            calculative, aiming for 30–50% profits. Strategic trading, though
            yielding lower targets (2–10% monthly), prioritises capital
            preservation and has a higher probability of success.{" "}
            <b>Unfortunately, over 98% of Indian traders fall into the blind or
            naked categories.</b>
          </P>

          <H4>Operators</H4>
          <P>
            Operators use substantial capital to manipulate prices — injecting
            or withdrawing significant funds from specific stocks. This
            manipulation, designed to induce panic and profit from swings, is
            illegal and thus conducted through hidden accounts. Their analysis
            focuses on understanding retailer behaviour — when and at what price
            retail investors will act. They scrutinise order flow and order
            books. Their operations are inherently unethical, inconsistent and
            highly risky.
          </P>

          <H4>Institutions</H4>
          <P>
            Institutions, managing vast pools of capital, employ highly
            strategic trading methods augmented by mathematical models and Greek
            parameters for robust position management. Consider this: if data
            suggests an upward trend and five retailers collectively buy call
            options anticipating a 20–50 point gain, institutions can profit by
            buying these options at a lower price and selling them at a
            marginal increase. Retailers, driven by FOMO, often execute market
            orders at inflated prices. <b>Institutions profit not from
            prediction but from exploiting retailers&rsquo; expectations</b> —
            who are willing to pay a premium for a perceived short-term gain.
          </P>
          <P>
            These opportunities, often fleeting, necessitate{" "}
            <b>algorithmic trading</b> for execution — highlighting
            technology&rsquo;s pivotal role. The profitability is staggering:
            FPIs and proprietary firms alone amassed <b>₹61,000 crore</b> in
            FY24, with 96–97% from algorithmic trading.
          </P>

          <H3>The numbers, side by side</H3>
          <Table
            headers={["Metric", "Value"]}
            rows={[
              ["Individual participants (current)", "10+ crore"],
              ["Active traders", "4.5 crore"],
              ["Derivative traders", "1 crore"],
              ["Projected participants by 2030-32", "35+ crore"],
              ["Retailers reporting losses", "93%"],
              ["Average individual retail loss", "₹2 lakh"],
              ["Retail F&O losses (FY24)", "₹75,000 crore"],
              ["FPI + prop trading profits (FY24)", "₹61,000 crore"],
              ["Share of profits from algo trading", "96–97%"],
            ]}
          />

          <H3>The unbeatable advantage: data and discipline</H3>
          <Quote>
            Data-driven decisions, governed by strict parameters and unwavering
            discipline, will invariably outperform emotional and
            prediction-based strategies in the long run.
          </Quote>
          <P>
            By adopting sophisticated strategies, rigorously managing risk and
            limiting losses, traders can significantly enhance their survival
            time and capitalise on market opportunities. Luck may initiate
            wealth creation in the market, but it is the disciplined mind that
            safeguards it for the opportune moment.
          </P>

          <Callout kind="success">
            <b>The takeaway:</b> Stop competing with institutions on prediction.
            You won&rsquo;t win that game. Compete on <b>discipline and
            execution</b> instead — and outsource execution to a system that
            doesn&rsquo;t feel FOMO at 2:47pm.
          </Callout>

          <div className="mt-12 rounded-2xl border border-[#084392]/20 bg-[#084392] text-white p-7">
            <h3 className="font-heading text-2xl md:text-3xl tracking-tight">
              That &ldquo;system&rdquo; is what we built.
            </h3>
            <p className="mt-3 text-white/80">
              Tradecure automates strategic, multi-leg trading for retail
              traders — risk in rupees, no code, no panic exits.
            </p>
            <a
              href="/#waitlist"
              data-testid="blog-cta-waitlist"
              className="mt-5 inline-flex items-center h-11 px-5 rounded-md bg-white text-[#084392] font-bold hover:bg-[#f8fafc] transition"
            >
              Join the waitlist
            </a>
          </div>
        </div>
      </article>

      <SiteFooterCompact />
    </main>
  );
}
